import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { Container } from '@carouselgg/nexus/container'
import { Section } from '@carouselgg/nexus/section'
import { Text } from '@carouselgg/nexus/text'
import { Input } from '@carouselgg/nexus/input'
import { Button } from '@carouselgg/nexus/button'
import { Spinner } from '@carouselgg/nexus/spinner'
import { Callout } from '@carouselgg/nexus/callout'
import { SEO } from '@carouselgg/nexus/seo'
import { Search } from '@carouselgg/nexus/icon'
import { FilterBar, FilterOptions, FilterToggle } from '@carouselgg/nexus/filter-bar'
import { SpotlightPanel } from '@carouselgg/nexus/spotlight-panel'
import { ActionLink } from '../components/ActionLink'
import { OrgCard } from '../components/OrgCard'
import { MyOrgsSection } from '../components/MyOrgsSection'
import { DiscoveryHero } from '../components/DiscoveryHero'
import { discoverOrganizations, getDiscoverGames } from '../api/discover'
import type { DiscoverOrg, DiscoverGameItem } from '../api/discover'
import styles from './OrgsPage.module.css'

const PAGE_SIZE = 24

/**
 * Org types, in the order the facet renders them. `organization` is the DB
 * value; "Club" is what everyone actually calls one.
 */
const TYPES = [
  { value: 'organization', label: 'Clubs' },
  { value: 'league', label: 'Leagues' },
  { value: 'university', label: 'Universities' },
] as const

const TYPE_VALUES = new Set<string>(TYPES.map((t) => t.value))

/**
 * One directory for every organization on Carousel.
 *
 * Clubs, leagues and university programs used to live at three addresses (two
 * of them byte-identical but for the type string sent to the API), which asked
 * the visitor to know the schema before they could browse. Type is a facet.
 */
export function OrgsPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  // `?type=` is the source of truth so the facet is linkable and survives a
  // reload — /orgs?type=university is the university directory.
  const typeParam = searchParams.get('type')
  const selectedType = typeParam && TYPE_VALUES.has(typeParam) ? typeParam : null

  const [games, setGames] = useState<DiscoverGameItem[]>([])
  const [selectedGame, setSelectedGame] = useState<string | null>(null)
  const [recruitingOnly, setRecruitingOnly] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [orgs, setOrgs] = useState<DiscoverOrg[]>([])
  const [total, setTotal] = useState(0)
  const [typeCounts, setTypeCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [gamesLoading, setGamesLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  function setType(next: string | null) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next) params.set('type', next)
        else params.delete('type')
        return params
      },
      { replace: true },
    )
  }

  useEffect(() => {
    debounceRef.current = setTimeout(() => { setSearch(searchInput) }, 300)
    return () => { clearTimeout(debounceRef.current) }
  }, [searchInput])

  // Game pills are scoped to the current type — filtering Universities by a
  // game no university plays would offer a filter that can only return nothing.
  useEffect(() => {
    let cancelled = false
    setGamesLoading(true)
    getDiscoverGames(selectedType ?? undefined)
      .then((g) => { if (!cancelled) { setGames(g ?? []); setGamesLoading(false) } })
      .catch(() => { if (!cancelled) setGamesLoading(false) })
    return () => { cancelled = true }
  }, [selectedType])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    discoverOrganizations({
      type: selectedType ?? undefined,
      game_id: selectedGame ?? undefined,
      q: search || undefined,
      limit: PAGE_SIZE,
      offset: 0,
    })
      .then((res) => {
        if (cancelled) return
        setOrgs(res.organizations ?? [])
        setTotal(res.total)
        setTypeCounts(res.type_counts ?? {})
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) { setOrgs([]); setTotal(0); setLoading(false) }
      })
    return () => { cancelled = true }
  }, [selectedType, selectedGame, search])

  function loadMore() {
    setLoadingMore(true)
    discoverOrganizations({
      type: selectedType ?? undefined,
      game_id: selectedGame ?? undefined,
      q: search || undefined,
      limit: PAGE_SIZE,
      offset: orgs.length,
    })
      .then((res) => {
        setOrgs((prev) => [...prev, ...(res.organizations ?? [])])
        setTotal(res.total)
        setLoadingMore(false)
      })
      .catch(() => { setLoadingMore(false) })
  }

  // "Recruiting" is a client-side narrowing of what is already loaded — the
  // API has no flag for it, and adding a round trip for a boolean the rows
  // already carry would be slower than filtering here.
  const visibleOrgs = recruitingOnly
    ? orgs.filter((o) => (o.type === 'league' ? o.registration_open : o.recruiting_open))
    : orgs


  // The featured slot goes to a recruiting org — the directory's whole job is
  // connecting people to somewhere that will actually take them.
  const featured = !loading && !search && !recruitingOnly
    ? visibleOrgs.find((o) => (o.type === 'league' ? o.registration_open : o.recruiting_open))
    : undefined
  const gridOrgs = featured ? visibleOrgs.filter((o) => o.id !== featured.id) : visibleOrgs

  const typeOptions = TYPES.map((t) => ({
    value: t.value,
    label: t.label,
    ...(typeCounts[t.value] != null ? { count: typeCounts[t.value] } : {}),
  }))

  const gameOptions = games.map((g) => ({
    value: g.id,
    label: g.name,
    ...(g.logo_url ? { icon: <img src={g.logo_url} alt="" /> } : {}),
  }))

  return (
    <div className={styles.page}>
      <SEO
        title="Organizations"
        description="Clubs, leagues and university esports programs on Carousel. Find a roster with an open spot, a league taking registrations, or a campus program near you."
      />

      <Container size="lg">
        <DiscoveryHero
          eyebrow="Organizations"
          title={<>Find your<br />people.</>}
          subtext="Clubs with open roster spots, leagues taking registrations, campus programs building a bench. Or put your own on the map — setup takes about five minutes."
          // Creating comes before browsing while Carousel is still filling up:
          // orgs are the customers and they bring the players, so a directory
          // of a few dozen is worth less than the next org to join it. The
          // count figures that used to sit here also undersold the platform.
          aside={
            <div className={styles.heroActions}>
              <ActionLink to="/create-org">Create an organization</ActionLink>
            </div>
          }
        />

        {/* "See what you get", split by type rather than guessed at.
            As a single hero button this pointed first at /for/leagues — a type
            the button beside it never asked for — and then at /how-it-works,
            which is a top-level nav destination with no route back here. Three
            named links let the reader pick, and each walkthrough carries a
            breadcrumb back to this directory. Sits above the results because
            creating matters more than browsing while Carousel fills up. */}
        <div className={styles.whatYouGet}>
          <p className={styles.whatYouGetLead}>See what you get</p>
          <div className={styles.whatYouGetLinks}>
            <ActionLink to="/for/clubs" variant="secondary">For clubs</ActionLink>
            <ActionLink to="/for/leagues" variant="secondary">For leagues</ActionLink>
            <ActionLink to="/for/universities" variant="secondary">For universities</ActionLink>
          </div>
        </div>

        <FilterBar
          primary={
            <FilterOptions
              label="Organization type"
              variant="segment"
              allLabel="All"
              {...(typeCounts.all != null ? { allCount: typeCounts.all } : {})}
              options={typeOptions}
              value={selectedType}
              onChange={setType}
            />
          }
          actions={
            <>
              <FilterToggle pressed={recruitingOnly} onChange={setRecruitingOnly}>
                Recruiting only
              </FilterToggle>
              <div className={styles.search}>
                <Input
                  placeholder="Search orgs"
                  leadIcon={<Search size={16} />}
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value) }}
                  aria-label="Search organizations"
                />
              </div>
            </>
          }
        >
          {gamesLoading ? (
            <Spinner size="sm" />
          ) : gameOptions.length > 0 && (
            <FilterOptions
              label="Game"
              allLabel="All games"
              options={gameOptions}
              value={selectedGame}
              onChange={setSelectedGame}
            />
          )}
        </FilterBar>
      </Container>

      <Section spacing="md">
        <Container size="lg">
          <MyOrgsSection {...(selectedType ? { type: selectedType } : {})} title="My Organizations" />
        </Container>
      </Section>

      {featured && (
        <Section spacing="md">
          <Container size="lg">
            <p className={styles.railLabel}>Featured</p>
            <SpotlightPanel
              {...(featured.primary_color ? { brandColor: featured.primary_color } : {})}
              logo={
                featured.logo_url
                  ? <img src={featured.logo_url} alt="" />
                  : <span>{featured.name.charAt(0)}</span>
              }
              titleSize="4xl"
              title={featured.name}
              meta={[
                `${featured.member_count} ${featured.member_count === 1 ? 'member' : 'members'}`,
                ...(featured.games.length > 0
                  ? [featured.games.map((g) => g.name).join(', ')]
                  : []),
              ]}
              aside={
                <Text size="sm">
                  {featured.type === 'league' ? 'Registration open' : 'Recruiting now'}
                </Text>
              }
              actions={<ActionLink to={`/org/${featured.slug}`}>View org</ActionLink>}
            >
              {featured.description && (
                <Text size="sm" muted>{featured.description}</Text>
              )}
            </SpotlightPanel>
          </Container>
        </Section>
      )}

      <Section spacing="lg">
        <Container size="lg">
          <div className={styles.listHead}>
            <Text as="h2" size="xl" weight="bold" className={styles.listTitle}>
              {selectedType
                ? TYPES.find((t) => t.value === selectedType)?.label
                : 'All organizations'}
            </Text>
            {total > 0 && <Text size="sm" muted>{total}</Text>}
          </div>

          {loading ? (
            <div className={styles.loadingWrap}><Spinner /></div>
          ) : gridOrgs.length === 0 ? (
            <Callout variant="info">
              {recruitingOnly
                ? 'None of these are recruiting right now. Turn off the filter to see them all.'
                : `No organizations found${search ? ` matching "${search}"` : selectedGame ? ' for this game' : ''}. Be the first to create one!`}
            </Callout>
          ) : (
            <>
              <div className={styles.grid}>
                {gridOrgs.map((org) => (
                  <OrgCard key={org.id} org={org} />
                ))}
              </div>
              {!recruitingOnly && total > orgs.length && (
                <div className={styles.loadMore}>
                  <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? 'Loading…' : `Show ${Math.min(PAGE_SIZE, total - orgs.length)} more`}
                  </Button>
                </div>
              )}
            </>
          )}
        </Container>
      </Section>

    </div>
  )
}
